import { useState, type CSSProperties } from 'react';
import { avatarColors, type AvatarColor } from '../../tokens/avatar';
import { cssVar } from '../../tokens/cssVar';
import { cx } from '../../utils/cx';
import s from './Avatar.module.css';

export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';

export type AvatarProps = {
  name: string;
  color: AvatarColor;
  size?: AvatarSize;
  /** Optional photo (e.g. from Google). Falls back to initials if it fails to load. */
  src?: string | null;
  /** Decorative when the name is already shown next to it (the default). */
  decorative?: boolean;
  className?: string;
};

/** Deterministic avatar color for an id (used when creating a Person). */
export function avatarColorFor(id: string): AvatarColor {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return avatarColors[h % avatarColors.length] as AvatarColor;
}

export const initialsOf = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
    .slice(0, 2);

export function Avatar({
  name,
  color,
  size = 'md',
  src,
  decorative = true,
  className,
}: AvatarProps) {
  const [broken, setBroken] = useState(false);
  const style = {
    '--bg': cssVar(`avatar.${color}.bg`),
    '--fg': cssVar(`avatar.${color}.fg`),
  } as CSSProperties;
  return (
    <span
      className={cx(s.avatar, className)}
      data-size={size}
      style={style}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : name}
      aria-hidden={decorative ? true : undefined}
    >
      {src && !broken ? (
        <img
          src={src}
          alt=""
          className={s.image}
          referrerPolicy="no-referrer"
          onError={() => setBroken(true)}
        />
      ) : (
        initialsOf(name)
      )}
    </span>
  );
}

export type AvatarGroupProps = {
  people: Array<{ id: string; name: string; color: AvatarColor; src?: string | null }>;
  /** Show at most this many; the rest become "+N". */
  max?: number;
  size?: 'sm' | 'md';
  className?: string;
};

export function AvatarGroup({ people, max = 4, size = 'sm', className }: AvatarGroupProps) {
  const shown = people.slice(0, max);
  const extra = people.length - shown.length;
  const names = people.map((p) => p.name).join(', ');
  return (
    <span className={cx(s.group, className)} role="img" aria-label={names} data-size={size}>
      {shown.map((p) => (
        <Avatar
          key={p.id}
          name={p.name}
          color={p.color}
          src={p.src}
          size={size}
          className={s.stacked}
        />
      ))}
      {extra > 0 ? (
        <span className={cx(s.avatar, s.stacked, s.more)} data-size={size} aria-hidden="true">
          +{extra}
        </span>
      ) : null}
    </span>
  );
}
